/**
 * DSL Parser for DiagramFoundry
 * Supports: 26 diagram types including AI/ML focused diagrams
 */

export function parseDSL(code) {
  if (!code || !code.trim()) return null;

  const lines = code.trim().split("\n").map((l) => l.trimEnd());
  const firstLine = lines[0].trim().toLowerCase();

  // Sorted alphabetically by type
  if (firstLine.startsWith("agent") || firstLine === "ai-agent") {
    return parseAIAgent(lines.slice(1));
  } else if (firstLine === "agentworkflow" || firstLine === "agent-workflow") {
    return parseAgentWorkflow(lines.slice(1));
  } else if (firstLine.startsWith("block")) {
    return parseBlock(lines.slice(1));
  } else if (firstLine.startsWith("c4")) {
    return parseC4(lines.slice(1));
  } else if (firstLine === "class") {
    return parseClassDiagram(lines.slice(1));
  } else if (firstLine === "dag") {
    return parseDAG(lines.slice(1));
  } else if (firstLine.startsWith("er") || firstLine === "erdiagram") {
    return parseER(lines.slice(1));
  } else if (firstLine.startsWith("git")) {
    return parseGit(lines.slice(1));
  } else if (firstLine === "gantt") {
    return parseGantt(lines.slice(1));
  } else if (firstLine.startsWith("graph") || firstLine.startsWith("flowchart")) {
    return parseFlowchart(lines);
  } else if (firstLine === "journey") {
    return parseJourney(lines.slice(1));
  } else if (firstLine === "kanban") {
    return parseKanban(lines.slice(1));
  } else if (firstLine === "knowledgegraph" || firstLine === "knowledge-graph" || firstLine === "kg") {
    return parseKnowledgeGraph(lines.slice(1));
  } else if (firstLine === "mindmap") {
    return parseMindmap(lines.slice(1));
  } else if (firstLine === "modelarch" || firstLine === "model-architecture") {
    return parseModelArch(lines.slice(1));
  } else if (firstLine.startsWith("packet")) {
    return parsePacket(lines.slice(1));
  } else if (firstLine === "pie") {
    return parsePie(lines.slice(1));
  } else if (firstLine === "pipeline" || firstLine === "ai-pipeline") {
    return parsePipeline(lines.slice(1));
  } else if (firstLine === "quadrant") {
    return parseQuadrant(lines.slice(1));
  } else if (firstLine === "requirements" || firstLine === "requirement") {
    return parseRequirements(lines.slice(1));
  } else if (firstLine === "sankey") {
    return parseSankey(lines.slice(1));
  } else if (firstLine === "sequence") {
    return parseSequence(lines.slice(1));
  } else if (firstLine.startsWith("state")) {
    return parseState(lines.slice(1));
  } else if (firstLine === "systemarch" || firstLine === "system-architecture") {
    return parseSystemArch(lines.slice(1));
  } else if (firstLine === "timeline") {
    return parseTimeline(lines.slice(1));
  } else if (firstLine === "architecture") {
    return parseFlowchart(["graph TD", ...lines.slice(1).map((l) => l.replace(/ -> /g, " --> "))]);
  }

  // Default: treat as flowchart
  return parseFlowchart(["graph TD", ...lines]);
}

function parseNodeText(text) {
  text = text.trim();
  if (!text) return null;

  // Match: ID[(label)] = database
  let match = text.match(/^(\w+)\[\((.+?)\)\]$/);
  if (match) return { id: match[1], label: match[2], shape: "database" };

  // Match: ID[label] = rectangle
  match = text.match(/^(\w+)\[(.+?)\]$/);
  if (match) return { id: match[1], label: match[2], shape: "rectangle" };

  // Match: ID{label} = diamond
  match = text.match(/^(\w+)\{(.+?)\}$/);
  if (match) return { id: match[1], label: match[2], shape: "diamond" };

  // Match: ID((label)) = circle
  match = text.match(/^(\w+)\(\((.+?)\)\)$/);
  if (match) return { id: match[1], label: match[2], shape: "circle" };

  // Match: ID(label) = rounded
  match = text.match(/^(\w+)\((.+?)\)$/);
  if (match) return { id: match[1], label: match[2], shape: "rounded" };

  // Match: bare ID
  match = text.match(/^(\w+)$/);
  if (match) return { id: match[1], label: match[1], shape: "rectangle" };

  return null;
}

function splitEdge(line) {
  const arrows = ["-->>", "-.->", "->>", "-->", "---", "->"];

  for (const arrow of arrows) {
    const idx = line.indexOf(arrow);
    if (idx === -1) continue;

    const left = line.substring(0, idx).trim();
    let right = line.substring(idx + arrow.length).trim();
    let edgeLabel = "";

    // Check for |label| on the right side
    const labelMatch = right.match(/^\|(.+?)\|\s*(.+)$/);
    if (labelMatch) {
      edgeLabel = labelMatch[1];
      right = labelMatch[2].trim();
    }

    return { left, right, arrow, edgeLabel };
  }

  return null;
}

function parseFlowchart(lines) {
  const dirMatch = lines[0].match(/(?:graph|flowchart)\s+(TD|TB|LR|RL|BT)/i);
  let direction = dirMatch ? dirMatch[1].toUpperCase() : "TD";
  if (direction === "TB") direction = "TD";

  const nodes = new Map();
  const edges = [];
  const subgraphs = [];
  let currentSubgraph = null;

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line || line.startsWith("//") || line.startsWith("%%")) continue;

    // Subgraph start
    if (line.toLowerCase().startsWith("subgraph ")) {
      const name = line.substring(9).trim();
      currentSubgraph = { id: `sg_${subgraphs.length}`, label: name, nodeIds: [] };
      continue;
    }

    // Subgraph end
    if (line.toLowerCase() === "end" && currentSubgraph) {
      subgraphs.push(currentSubgraph);
      currentSubgraph = null;
      continue;
    }

    // Try edge parse
    const edgeParts = splitEdge(line);
    if (edgeParts) {
      const source = parseNodeText(edgeParts.left);
      const target = parseNodeText(edgeParts.right);

      if (source && target) {
        if (!nodes.has(source.id)) nodes.set(source.id, source);
        else if (source.label !== source.id) {
          nodes.get(source.id).label = source.label;
          nodes.get(source.id).shape = source.shape;
        }

        if (!nodes.has(target.id)) nodes.set(target.id, target);
        else if (target.label !== target.id) {
          nodes.get(target.id).label = target.label;
          nodes.get(target.id).shape = target.shape;
        }

        const edgeStyle = edgeParts.arrow === "-.->" ? "dashed" : "solid";
        edges.push({
          source: source.id,
          target: target.id,
          label: edgeParts.edgeLabel,
          style: edgeStyle,
        });

        if (currentSubgraph) {
          if (!currentSubgraph.nodeIds.includes(source.id))
            currentSubgraph.nodeIds.push(source.id);
          if (!currentSubgraph.nodeIds.includes(target.id))
            currentSubgraph.nodeIds.push(target.id);
        }
        continue;
      }
    }

    // Standalone node
    const node = parseNodeText(line);
    if (node) {
      if (!nodes.has(node.id)) nodes.set(node.id, node);
      if (currentSubgraph) currentSubgraph.nodeIds.push(node.id);
    }
  }

  return {
    type: "flowchart",
    direction,
    nodes: Array.from(nodes.values()),
    edges,
    subgraphs,
  };
}

function parseSequence(lines) {
  const actors = new Map();
  const messages = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    // Pattern: Actor ->> Actor: Message
    const msgMatch = trimmed.match(/^(.+?)\s*(-->>|->>|-->|->)\s*(.+?):\s*(.+)$/);
    if (msgMatch) {
      const from = msgMatch[1].trim();
      const to = msgMatch[3].trim();
      const type = msgMatch[2].startsWith("--") ? "reply" : "request";
      const message = msgMatch[4].trim();

      if (!actors.has(from)) actors.set(from, { id: from, label: from });
      if (!actors.has(to)) actors.set(to, { id: to, label: to });

      messages.push({ from, to, message, type });
    }
  }

  return {
    type: "sequence",
    actors: Array.from(actors.values()),
    messages,
  };
}


function parseClassDiagram(lines) {
  const classes = [];
  const relationships = [];
  let currentClass = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const classMatch = trimmed.match(/^class\s+(\w+)\s*\{?$/);
    if (classMatch) {
      currentClass = { name: classMatch[1], properties: [], methods: [] };
      classes.push(currentClass);
      continue;
    }

    if (trimmed === "}" && currentClass) {
      currentClass = null;
      continue;
    }

    if (currentClass) {
      const memberMatch = trimmed.match(/^([+\-#~])(\w+)(?:\(([^)]*)\))?\s*:?\s*(.*)$/);
      if (memberMatch) {
        const visibility = memberMatch[1];
        const name = memberMatch[2];
        const params = memberMatch[3];
        const typeOrReturn = memberMatch[4]?.trim() || "";
        if (params !== undefined) {
          currentClass.methods.push({ visibility, name, params: params || "", returnType: typeOrReturn });
        } else {
          currentClass.properties.push({ visibility, name, type: typeOrReturn });
        }
        continue;
      }
    }

    const relMatch = trimmed.match(/^(\w+)\s*(<\|--|\*--|o--|-->|\.\.>|--)\s*(\w+)(?:\s*:\s*(.+))?$/);
    if (relMatch) {
      relationships.push({ from: relMatch[1], to: relMatch[3], type: relMatch[2], label: relMatch[4] || "" });
    }
  }

  return { type: "class", classes, relationships };
}

function parseGantt(lines) {
  let title = "Gantt Chart";
  const sections = [];
  let currentSection = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const titleMatch = trimmed.match(/^title\s+(.+)$/);
    if (titleMatch) { title = titleMatch[1]; continue; }

    const sectionMatch = trimmed.match(/^section\s+(.+)$/);
    if (sectionMatch) {
      currentSection = { name: sectionMatch[1], tasks: [] };
      sections.push(currentSection);
      continue;
    }

    const taskMatch = trimmed.match(/^(.+?):\s*(\d{4}-\d{2}-\d{2})\s*,\s*(\d+)d$/);
    if (taskMatch && currentSection) {
      currentSection.tasks.push({
        name: taskMatch[1].trim(),
        start: taskMatch[2],
        duration: parseInt(taskMatch[3]),
      });
    }
  }

  return { type: "gantt", title, sections };
}

function parseKanban(lines) {
  const columns = [];
  let currentColumn = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const colMatch = trimmed.match(/^column\s+(.+)$/);
    if (colMatch) {
      currentColumn = { name: colMatch[1], items: [] };
      columns.push(currentColumn);
      continue;
    }

    if (currentColumn && trimmed) {
      currentColumn.items.push(trimmed);
    }
  }

  return { type: "kanban", columns };
}

function parseQuadrant(lines) {
  let title = "Quadrant Chart";
  let xAxis = { left: "Low", right: "High" };
  let yAxis = { bottom: "Low", top: "High" };
  const quadrantLabels = ["", "", "", ""];
  const items = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const titleMatch = trimmed.match(/^title\s+(.+)$/);
    if (titleMatch) { title = titleMatch[1]; continue; }

    const xMatch = trimmed.match(/^x-axis\s+(.+?)\s*-->\s*(.+)$/);
    if (xMatch) { xAxis = { left: xMatch[1], right: xMatch[2] }; continue; }

    const yMatch = trimmed.match(/^y-axis\s+(.+?)\s*-->\s*(.+)$/);
    if (yMatch) { yAxis = { bottom: yMatch[1], top: yMatch[2] }; continue; }

    const qMatch = trimmed.match(/^quadrant-([1-4])\s+(.+)$/);
    if (qMatch) { quadrantLabels[parseInt(qMatch[1]) - 1] = qMatch[2]; continue; }

    const itemMatch = trimmed.match(/^(.+?):\s*\[(\d*\.?\d+)\s*,\s*(\d*\.?\d+)\]$/);
    if (itemMatch) {
      items.push({ name: itemMatch[1].trim(), x: parseFloat(itemMatch[2]), y: parseFloat(itemMatch[3]) });
    }
  }

  return { type: "quadrant", title, xAxis, yAxis, quadrantLabels, items };
}

function parseRequirements(lines) {
  const requirements = [];
  const relationships = [];
  let currentReq = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const reqMatch = trimmed.match(/^req\s+(.+?)\s*\{$/);
    if (reqMatch) {
      currentReq = { name: reqMatch[1], id: "", priority: "", status: "" };
      requirements.push(currentReq);
      continue;
    }

    if (trimmed === "}" && currentReq) { currentReq = null; continue; }

    if (currentReq) {
      const propMatch = trimmed.match(/^(\w+):\s*(.+)$/);
      if (propMatch) { currentReq[propMatch[1]] = propMatch[2]; }
      continue;
    }

    const relMatch = trimmed.match(/^(\S+)\s*-\s*(\w+)\s*->\s*(\S+)$/);
    if (relMatch) {
      relationships.push({ from: relMatch[1], type: relMatch[2], to: relMatch[3] });
    }
  }

  return { type: "requirements", requirements, relationships };
}

function parseJourney(lines) {
  let title = "User Journey";
  const sections = [];
  let currentSection = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const titleMatch = trimmed.match(/^title\s+(.+)$/);
    if (titleMatch) { title = titleMatch[1]; continue; }

    const sectionMatch = trimmed.match(/^section\s+(.+)$/);
    if (sectionMatch) {
      currentSection = { name: sectionMatch[1], steps: [] };
      sections.push(currentSection);
      continue;
    }

    const stepMatch = trimmed.match(/^(.+?):\s*(\d+)\s*:\s*(.+)$/);
    if (stepMatch && currentSection) {
      currentSection.steps.push({
        name: stepMatch[1].trim(),
        score: parseInt(stepMatch[2]),
        actor: stepMatch[3].trim(),
      });
    }
  }

  return { type: "journey", title, sections };
}

// --- NEW DIAGRAM TYPES ---

function parsePie(lines) {
  let title = "Pie Chart";
  const slices = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const titleMatch = trimmed.match(/^title\s+(.+)$/);
    if (titleMatch) { title = titleMatch[1]; continue; }

    // Pattern: "Label" : value
    const sliceMatch = trimmed.match(/^"(.+?)"\s*:\s*(\d+\.?\d*)$/);
    if (sliceMatch) {
      slices.push({ label: sliceMatch[1], value: parseFloat(sliceMatch[2]) });
    }
  }

  return { type: "pie", title, slices };
}

function parseMindmap(lines) {
  const root = { label: "Root", children: [] };
  const stack = [{ node: root, indent: -1 }];

  for (const line of lines) {
    if (!line.trim() || line.trim().startsWith("//")) continue;

    const indent = line.search(/\S/);
    const label = line.trim();

    const newNode = { label, children: [] };

    // Find parent based on indent
    while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
      stack.pop();
    }

    stack[stack.length - 1].node.children.push(newNode);
    stack.push({ node: newNode, indent });
  }

  return { type: "mindmap", root: root.children[0] || root };
}

function parseSankey(lines) {
  let title = "Sankey Diagram";
  const flows = [];
  const nodes = new Set();

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const titleMatch = trimmed.match(/^title\s+(.+)$/);
    if (titleMatch) { title = titleMatch[1]; continue; }

    // Pattern: Source,Target,Value
    const flowMatch = trimmed.match(/^(.+?),(.+?),(\d+\.?\d*)$/);
    if (flowMatch) {
      const source = flowMatch[1].trim();
      const target = flowMatch[2].trim();
      nodes.add(source);
      nodes.add(target);
      flows.push({ source, target, value: parseFloat(flowMatch[3]) });
    }
  }

  return { type: "sankey", title, nodes: Array.from(nodes), flows };
}

function parseTimeline(lines) {
  let title = "Timeline";
  const events = [];
  let currentSection = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const titleMatch = trimmed.match(/^title\s+(.+)$/);
    if (titleMatch) { title = titleMatch[1]; continue; }

    const sectionMatch = trimmed.match(/^section\s+(.+)$/);
    if (sectionMatch) {
      currentSection = sectionMatch[1];
      continue;
    }

    // Pattern: date : event description
    const eventMatch = trimmed.match(/^(.+?)\s*:\s*(.+)$/);
    if (eventMatch) {
      events.push({
        date: eventMatch[1].trim(),
        description: eventMatch[2].trim(),
        section: currentSection
      });
    }
  }

  return { type: "timeline", title, events };
}

function parseState(lines) {
  const states = [];
  const transitions = [];
  let currentState = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    // State definition: state "Name" as alias
    const stateMatch = trimmed.match(/^state\s+"(.+?)"\s+as\s+(\w+)$/);
    if (stateMatch) {
      states.push({ id: stateMatch[2], label: stateMatch[1], type: "state" });
      continue;
    }

    // Simple state: stateName
    const simpleStateMatch = trimmed.match(/^\[(\*)\]$/);
    if (simpleStateMatch) {
      states.push({ id: "_start", label: "[*]", type: "start" });
      continue;
    }

    // Transition: state1 --> state2 : label
    const transMatch = trimmed.match(/^(\S+)\s*(-->|->)\s*(\S+)(?:\s*:\s*(.+))?$/);
    if (transMatch) {
      const from = transMatch[1] === "[*]" ? "_start" : transMatch[1];
      const to = transMatch[3] === "[*]" ? "_end" : transMatch[3];
      
      // Add states if not exists
      if (!states.find(s => s.id === from) && from !== "_start") {
        states.push({ id: from, label: from, type: "state" });
      }
      if (!states.find(s => s.id === to) && to !== "_end") {
        states.push({ id: to, label: to, type: "state" });
      }
      if (from === "_start" && !states.find(s => s.id === "_start")) {
        states.push({ id: "_start", label: "[*]", type: "start" });
      }
      if (to === "_end" && !states.find(s => s.id === "_end")) {
        states.push({ id: "_end", label: "[*]", type: "end" });
      }
      
      transitions.push({ from, to, label: transMatch[4] || "" });
    }
  }

  return { type: "state", states, transitions };
}

function parseER(lines) {
  const entities = [];
  const relationships = [];
  let currentEntity = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    // Entity with attributes: ENTITY { attr type }
    const entityMatch = trimmed.match(/^(\w+)\s*\{$/);
    if (entityMatch) {
      currentEntity = { name: entityMatch[1], attributes: [] };
      entities.push(currentEntity);
      continue;
    }

    if (trimmed === "}" && currentEntity) {
      currentEntity = null;
      continue;
    }

    // Attribute: type name PK/FK
    if (currentEntity) {
      const attrMatch = trimmed.match(/^(\w+)\s+(\w+)(?:\s+(PK|FK|UK))?$/);
      if (attrMatch) {
        currentEntity.attributes.push({
          type: attrMatch[1],
          name: attrMatch[2],
          key: attrMatch[3] || null
        });
      }
      continue;
    }

    // Relationship: Entity1 ||--o{ Entity2 : label
    const relMatch = trimmed.match(/^(\w+)\s*(\|\|--o\{|\|\|--\|\||\}o--o\{|--)\s*(\w+)(?:\s*:\s*(.+))?$/);
    if (relMatch) {
      relationships.push({
        from: relMatch[1],
        to: relMatch[3],
        type: relMatch[2],
        label: relMatch[4] || ""
      });
    }
  }

  return { type: "er", entities, relationships };
}

function parseBlock(lines) {
  const blocks = [];
  let currentBlock = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    // Block definition
    const blockMatch = trimmed.match(/^block\s+(\w+)(?:\s+"(.+?)")?$/);
    if (blockMatch) {
      currentBlock = { id: blockMatch[1], label: blockMatch[2] || blockMatch[1], children: [] };
      blocks.push(currentBlock);
      continue;
    }

    // Nested block or item
    if (currentBlock && trimmed) {
      currentBlock.children.push(trimmed);
    }
  }

  return { type: "block", blocks };
}

function parseC4(lines) {
  let title = "C4 Diagram";
  const elements = [];
  const relationships = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const titleMatch = trimmed.match(/^title\s+(.+)$/);
    if (titleMatch) { title = titleMatch[1]; continue; }

    // Person/System/Container/Component
    const elemMatch = trimmed.match(/^(Person|System|Container|Component|Database)\((\w+),\s*"(.+?)"(?:,\s*"(.+?)")?\)$/);
    if (elemMatch) {
      elements.push({
        type: elemMatch[1].toLowerCase(),
        id: elemMatch[2],
        label: elemMatch[3],
        description: elemMatch[4] || ""
      });
      continue;
    }

    // Relationship: Rel(from, to, "label")
    const relMatch = trimmed.match(/^Rel\((\w+),\s*(\w+),\s*"(.+?)"\)$/);
    if (relMatch) {
      relationships.push({
        from: relMatch[1],
        to: relMatch[2],
        label: relMatch[3]
      });
    }
  }

  return { type: "c4", title, elements, relationships };
}

function parseGit(lines) {
  const commits = [];
  const branches = new Set(["main"]);
  let currentBranch = "main";

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    // commit
    const commitMatch = trimmed.match(/^commit(?:\s+id:\s*"(.+?)")?(?:\s+msg:\s*"(.+?)")?$/);
    if (commitMatch) {
      commits.push({
        id: commitMatch[1] || `c${commits.length + 1}`,
        message: commitMatch[2] || `Commit ${commits.length + 1}`,
        branch: currentBranch
      });
      continue;
    }

    // branch
    const branchMatch = trimmed.match(/^branch\s+(\w+)$/);
    if (branchMatch) {
      branches.add(branchMatch[1]);
      currentBranch = branchMatch[1];
      continue;
    }

    // checkout
    const checkoutMatch = trimmed.match(/^checkout\s+(\w+)$/);
    if (checkoutMatch) {
      currentBranch = checkoutMatch[1];
      continue;
    }

    // merge
    const mergeMatch = trimmed.match(/^merge\s+(\w+)$/);
    if (mergeMatch) {
      commits.push({
        id: `merge_${commits.length + 1}`,
        message: `Merge ${mergeMatch[1]}`,
        branch: currentBranch,
        merge: mergeMatch[1]
      });
    }
  }

  return { type: "git", branches: Array.from(branches), commits };
}

function parsePacket(lines) {
  let title = "Packet Diagram";
  const fields = [];
  let bitOffset = 0;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const titleMatch = trimmed.match(/^title\s+(.+)$/);
    if (titleMatch) { title = titleMatch[1]; continue; }

    // Field: start-end: "label"
    const fieldMatch = trimmed.match(/^(\d+)-(\d+)\s*:\s*"(.+?)"$/);
    if (fieldMatch) {
      fields.push({
        start: parseInt(fieldMatch[1]),
        end: parseInt(fieldMatch[2]),
        label: fieldMatch[3]
      });
    } else {
      // Simple field: "label" : bits
      const simpleMatch = trimmed.match(/^"(.+?)"\s*:\s*(\d+)$/);
      if (simpleMatch) {
        const bits = parseInt(simpleMatch[2]);
        fields.push({
          start: bitOffset,
          end: bitOffset + bits - 1,
          label: simpleMatch[1]
        });
        bitOffset += bits;
      }
    }
  }

  return { type: "packet", title, fields };
}

// --- AI/ML DIAGRAM TYPES ---

function parseAIAgent(lines) {
  let title = "AI Agent Architecture";
  const components = [];
  const connections = [];

  const componentTypes = {
    user: { color: "#3b82f6", icon: "user" },
    agent: { color: "#8b5cf6", icon: "bot" },
    llm: { color: "#22c55e", icon: "brain" },
    model: { color: "#22c55e", icon: "brain" },
    tool: { color: "#f59e0b", icon: "wrench" },
    tools: { color: "#f59e0b", icon: "wrench" },
    memory: { color: "#ec4899", icon: "database" },
    vectordb: { color: "#06b6d4", icon: "database" },
    api: { color: "#ef4444", icon: "cloud" },
    environment: { color: "#64748b", icon: "box" }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const titleMatch = trimmed.match(/^title\s+(.+)$/);
    if (titleMatch) { title = titleMatch[1]; continue; }

    // Component: [type] name "description"
    const compMatch = trimmed.match(/^\[(\w+)\]\s+(\w+)(?:\s+"(.+?)")?$/);
    if (compMatch) {
      const type = compMatch[1].toLowerCase();
      components.push({
        id: compMatch[2],
        type,
        label: compMatch[2],
        description: compMatch[3] || "",
        ...componentTypes[type] || { color: "#94a3b8", icon: "box" }
      });
      continue;
    }

    // Connection: from --> to : label
    const connMatch = trimmed.match(/^(\w+)\s*(-->|->|<-->|<->)\s*(\w+)(?:\s*:\s*(.+))?$/);
    if (connMatch) {
      connections.push({
        from: connMatch[1],
        to: connMatch[3],
        bidirectional: connMatch[2].includes("<"),
        label: connMatch[4] || ""
      });
    }
  }

  return { type: "ai-agent", title, components, connections };
}

function parseAgentWorkflow(lines) {
  let title = "Agent Workflow";
  const nodes = [];
  const edges = [];
  let currentSection = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const titleMatch = trimmed.match(/^title\s+(.+)$/);
    if (titleMatch) { title = titleMatch[1]; continue; }

    const sectionMatch = trimmed.match(/^section\s+(.+)$/);
    if (sectionMatch) { currentSection = sectionMatch[1]; continue; }

    // Node with type: [decision] NodeName "Label"
    const nodeMatch = trimmed.match(/^\[(\w+)\]\s+(\w+)(?:\s+"(.+?)")?$/);
    if (nodeMatch) {
      nodes.push({
        id: nodeMatch[2],
        type: nodeMatch[1].toLowerCase(),
        label: nodeMatch[3] || nodeMatch[2],
        section: currentSection
      });
      continue;
    }

    // Simple node: NodeName "Label"
    const simpleNodeMatch = trimmed.match(/^(\w+)\s+"(.+?)"$/);
    if (simpleNodeMatch) {
      nodes.push({
        id: simpleNodeMatch[1],
        type: "task",
        label: simpleNodeMatch[2],
        section: currentSection
      });
      continue;
    }

    // Edge: from --> to : label
    const edgeMatch = trimmed.match(/^(\w+)\s*(-->|->|-->\|loop\||-->\|yes\||-->\|no\|)\s*(\w+)(?:\s*:\s*(.+))?$/);
    if (edgeMatch) {
      const edgeType = edgeMatch[2];
      edges.push({
        from: edgeMatch[1],
        to: edgeMatch[3],
        label: edgeMatch[4] || (edgeType.includes("loop") ? "loop" : edgeType.includes("yes") ? "yes" : edgeType.includes("no") ? "no" : ""),
        type: edgeType.includes("loop") ? "loop" : "normal"
      });
    }
  }

  return { type: "agent-workflow", title, nodes, edges };
}

function parseDAG(lines) {
  let title = "DAG";
  const tasks = [];
  const dependencies = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const titleMatch = trimmed.match(/^title\s+(.+)$/);
    if (titleMatch) { title = titleMatch[1]; continue; }

    // Task definition: task TaskName "Label" [status]
    const taskMatch = trimmed.match(/^task\s+(\w+)(?:\s+"(.+?)")?(?:\s+\[(\w+)\])?$/);
    if (taskMatch) {
      tasks.push({
        id: taskMatch[1],
        label: taskMatch[2] || taskMatch[1],
        status: taskMatch[3] || "pending"
      });
      continue;
    }

    // Dependency: from --> to
    const depMatch = trimmed.match(/^(\w+)\s*(-->|->)\s*(\w+)$/);
    if (depMatch) {
      // Auto-create tasks if not exists
      if (!tasks.find(t => t.id === depMatch[1])) {
        tasks.push({ id: depMatch[1], label: depMatch[1], status: "pending" });
      }
      if (!tasks.find(t => t.id === depMatch[3])) {
        tasks.push({ id: depMatch[3], label: depMatch[3], status: "pending" });
      }
      dependencies.push({ from: depMatch[1], to: depMatch[3] });
    }
  }

  return { type: "dag", title, tasks, dependencies };
}

function parsePipeline(lines) {
  let title = "AI Pipeline";
  const stages = [];
  const flows = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const titleMatch = trimmed.match(/^title\s+(.+)$/);
    if (titleMatch) { title = titleMatch[1]; continue; }

    // Stage: [type] name "label"
    const stageMatch = trimmed.match(/^\[(\w+)\]\s+(\w+)(?:\s+"(.+?)")?$/);
    if (stageMatch) {
      stages.push({
        id: stageMatch[2],
        type: stageMatch[1].toLowerCase(),
        label: stageMatch[3] || stageMatch[2]
      });
      continue;
    }

    // Flow: from --> to : label
    const flowMatch = trimmed.match(/^(\w+)\s*(-->|->)\s*(\w+)(?:\s*:\s*(.+))?$/);
    if (flowMatch) {
      // Auto-create stages if not exists
      if (!stages.find(s => s.id === flowMatch[1])) {
        stages.push({ id: flowMatch[1], type: "process", label: flowMatch[1] });
      }
      if (!stages.find(s => s.id === flowMatch[3])) {
        stages.push({ id: flowMatch[3], type: "process", label: flowMatch[3] });
      }
      flows.push({
        from: flowMatch[1],
        to: flowMatch[3],
        label: flowMatch[4] || ""
      });
    }
  }

  return { type: "pipeline", title, stages, flows };
}

function parseKnowledgeGraph(lines) {
  let title = "Knowledge Graph";
  const entities = [];
  const relations = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const titleMatch = trimmed.match(/^title\s+(.+)$/);
    if (titleMatch) { title = titleMatch[1]; continue; }

    // Entity: entity Name [type] "description"
    const entityMatch = trimmed.match(/^entity\s+(\w+)(?:\s+\[(\w+)\])?(?:\s+"(.+?)")?$/);
    if (entityMatch) {
      entities.push({
        id: entityMatch[1],
        label: entityMatch[1],
        type: entityMatch[2] || "entity",
        description: entityMatch[3] || ""
      });
      continue;
    }

    // Relation: Entity1 -[relation]-> Entity2
    const relMatch = trimmed.match(/^(\w+)\s+-\[(.+?)\]->\s*(\w+)$/);
    if (relMatch) {
      // Auto-create entities if not exists
      if (!entities.find(e => e.id === relMatch[1])) {
        entities.push({ id: relMatch[1], label: relMatch[1], type: "entity" });
      }
      if (!entities.find(e => e.id === relMatch[3])) {
        entities.push({ id: relMatch[3], label: relMatch[3], type: "entity" });
      }
      relations.push({
        from: relMatch[1],
        relation: relMatch[2],
        to: relMatch[3]
      });
    }
  }

  return { type: "knowledge-graph", title, entities, relations };
}

function parseModelArch(lines) {
  let title = "Model Architecture";
  const layers = [];
  const connections = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const titleMatch = trimmed.match(/^title\s+(.+)$/);
    if (titleMatch) { title = titleMatch[1]; continue; }

    // Layer: layer Name [type] "shape"
    const layerMatch = trimmed.match(/^layer\s+(\w+)(?:\s+\[(\w+)\])?(?:\s+"(.+?)")?$/);
    if (layerMatch) {
      layers.push({
        id: layerMatch[1],
        label: layerMatch[1],
        type: layerMatch[2] || "dense",
        shape: layerMatch[3] || ""
      });
      continue;
    }

    // Connection: from --> to
    const connMatch = trimmed.match(/^(\w+)\s*(-->|->)\s*(\w+)$/);
    if (connMatch) {
      // Auto-create layers if not exists
      if (!layers.find(l => l.id === connMatch[1])) {
        layers.push({ id: connMatch[1], label: connMatch[1], type: "dense" });
      }
      if (!layers.find(l => l.id === connMatch[3])) {
        layers.push({ id: connMatch[3], label: connMatch[3], type: "dense" });
      }
      connections.push({ from: connMatch[1], to: connMatch[3] });
    }
  }

  return { type: "model-arch", title, layers, connections };
}

function parseSystemArch(lines) {
  let title = "System Architecture";
  const components = [];
  const connections = [];
  let currentLayer = null;

  const layerColors = {
    frontend: "#3b82f6",
    backend: "#22c55e",
    data: "#f59e0b",
    ai: "#8b5cf6",
    external: "#64748b"
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const titleMatch = trimmed.match(/^title\s+(.+)$/);
    if (titleMatch) { title = titleMatch[1]; continue; }

    const layerMatch = trimmed.match(/^layer\s+(\w+)$/);
    if (layerMatch) { currentLayer = layerMatch[1].toLowerCase(); continue; }

    // Component: [type] name "label"
    const compMatch = trimmed.match(/^\[(\w+)\]\s+(\w+)(?:\s+"(.+?)")?$/);
    if (compMatch) {
      components.push({
        id: compMatch[2],
        type: compMatch[1].toLowerCase(),
        label: compMatch[3] || compMatch[2],
        layer: currentLayer,
        color: layerColors[currentLayer] || "#94a3b8"
      });
      continue;
    }

    // Connection: from --> to : label
    const connMatch = trimmed.match(/^(\w+)\s*(-->|->|<-->)\s*(\w+)(?:\s*:\s*(.+))?$/);
    if (connMatch) {
      connections.push({
        from: connMatch[1],
        to: connMatch[3],
        bidirectional: connMatch[2].includes("<"),
        label: connMatch[4] || ""
      });
    }
  }

  return { type: "system-arch", title, components, connections };
}

